import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { APP_INITIALIZER, ApplicationConfig, provideZoneChangeDetection } from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter } from '@angular/router';
import { provideToastr } from 'ngx-toastr';

import { authInterceptor } from './core/interceptors/auth.interceptor';
import { AuthService } from './core/services/auth.service';
import { routes } from './app.routes';

/** How long app boot waits for identity-v2 before rendering anyway; guards still await the real outcome. */
export const AUTH_BOOTSTRAP_TIMEOUT_MS = 8000;

/**
 * Picks up the identity-v2 session (or a legacy token) and loads the Pass account before the first route, so
 * guards can check the role. Public badge pages skip it entirely; everywhere else the wait is capped so an
 * unreachable identity never leaves the app blank.
 */
export function initializeAuth(authService: AuthService): () => Promise<void> {
  return async () => {
    if (window.location.pathname.startsWith('/badge')) {
      return;
    }
    const bootstrap = authService.bootstrap();
    let timer: ReturnType<typeof setTimeout> | undefined;
    const timeout = new Promise<void>((resolve) => {
      timer = setTimeout(resolve, AUTH_BOOTSTRAP_TIMEOUT_MS);
    });
    await Promise.race([bootstrap, timeout]);
    clearTimeout(timer);
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideHttpClient(withInterceptors([authInterceptor])),
    provideAnimations(),
    provideToastr({
      positionClass: 'toast-top-right',
      timeOut: 5000,
      preventDuplicates: true,
      newestOnTop: true,
      maxOpened: 3,
      autoDismiss: true,
    }),
    {
      provide: APP_INITIALIZER,
      useFactory: initializeAuth,
      deps: [AuthService],
      multi: true,
    },
  ],
};
